import { useFetchConnectedIntegrations } from '@modules/integrationsV2/hooks';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getIsCohortIntegrationAlreadyConnected,
  getIsUserOrAccountIntegrationAlreadyConnected,
} from '@modules/integrationsV2/utils';
import {
  CheckCircleRounded,
  EastRounded,
  InfoRounded,
} from '@mui/icons-material';
import { Box, Chip, Link, Stack, Tooltip } from '@mui/material';
import { IntegrationChannelType, IntegrationType } from 'generated/graphql';
import { toTitleCase } from 'v2/utils/strings';

interface ConnectedIntegrationChipProps {
  integrationType: IntegrationType;
  channelType: IntegrationChannelType;
}

export const ConnectedIntegrationChip = ({
  integrationType,
  channelType,
}: ConnectedIntegrationChipProps) => {
  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first',
  });

  const isCohortIntegrationAlreadyConnected =
    getIsCohortIntegrationAlreadyConnected({
      connectedIntegrations,
      channelType,
    });

  const {
    isUserOrAccountIntegrationConnected,
    isSameSource: isSameSourceUserOrAccountIntegration,
    connectedIntegration: connectedUserOrAccountIntegration,
  } = getIsUserOrAccountIntegrationAlreadyConnected({
    connectedIntegrations,
    integrationType,
    channelType,
  });

  if (
    !isCohortIntegrationAlreadyConnected &&
    !isUserOrAccountIntegrationConnected
  ) {
    return null;
  }

  const activeIntegration =
    connectedUserOrAccountIntegration ??
    connectedIntegrations.find(
      (integration) =>
        integration.channel.type === channelType &&
        integration.integrationType === integrationType &&
        integration.isActive,
    );

  if (!activeIntegration) {
    return null;
  }

  const goToIntegrationDetailView = () => {
    dispatch({
      type: IntegrationsActionType.SET_VIEW,
      payload: {
        selectedIntegrationSourceOption: {
          integrationType: activeIntegration.integrationType,
          channelType: activeIntegration.channel.type,
          integrationID: activeIntegration.id,
          namespace: activeIntegration.namespace,
        },
        isAddDrawerOpen: false,
      },
    });
  };

  const integrationDisplayName = activeIntegration.channel.displayName;

  const openIntegrationDetailsAction = () => {
    return (
      <Link
        onClick={goToIntegrationDetailView}
        underline="hover"
        sx={{
          cursor: 'pointer',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box color="inherit">Manage {integrationDisplayName} Integration</Box>
          <EastRounded
            color="inherit"
            sx={{
              fontSize: '0.8rem',
            }}
          />
        </Stack>
      </Link>
    );
  };

  const getTooltipTitle = () => {
    if (isCohortIntegrationAlreadyConnected) {
      return (
        <Stack rowGap={1}>
          <Box>
            You can only connect one integration for {integrationDisplayName}.
            Disable the current {integrationDisplayName} integration to add
            another.
          </Box>
          {openIntegrationDetailsAction()}
        </Stack>
      );
    }

    if (isUserOrAccountIntegrationConnected) {
      return (
        <Stack rowGap={1}>
          <Box>
            You can only connect one {toTitleCase(integrationType)} integration.{' '}
            {integrationDisplayName} is already connected. Disable the{' '}
            {integrationDisplayName} {toTitleCase(integrationType)} integration
            to add another.
          </Box>
          {openIntegrationDetailsAction()}
        </Stack>
      );
    }

    return null;
  };

  const shouldShowAsNotAllowed =
    isUserOrAccountIntegrationConnected &&
    !isSameSourceUserOrAccountIntegration;

  return (
    <Tooltip arrow title={getTooltipTitle()} placement="top">
      <Chip
        variant={shouldShowAsNotAllowed ? 'filled' : 'outlined'}
        color={shouldShowAsNotAllowed ? 'default' : 'success'}
        label={shouldShowAsNotAllowed ? 'Not Allowed' : 'Connected'}
        icon={shouldShowAsNotAllowed ? <InfoRounded /> : <CheckCircleRounded />}
      />
    </Tooltip>
  );
};
